import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Shell = ({ children }) => (
  <div className="grain min-h-screen bg-[#08090E]">
    <Header />
    <main className="relative z-10 mx-auto grid min-h-screen max-w-lg place-items-center px-4">
      <div className="card fade-up w-full p-8 text-center" data-testid="payment-status-message">{children}</div>
    </main>
  </div>
);

const BackBtn = () => (
  <Link to="/dashboard" data-testid="back-to-dashboard-button" className="mt-8 inline-block rounded-full bg-[#F7931A] px-6 py-3 font-semibold text-[#08090E] transition-colors hover:bg-amber-500">Back to dashboard</Link>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const [kind, setKind] = useState("buy");

  useEffect(() => {
    let tries = 0, alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (!alive) return;
        setKind(data.kind || "buy");
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      if (++tries >= 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [sid, refresh]);

  if (state === "paid") return (
    <Shell><CheckCircle2 className="mx-auto text-emerald-400" size={56} />
      <h1 className="mt-4 text-2xl font-bold tracking-tight">{kind === "subscription" ? "Auto-buy activated" : "Bitcoin purchased"}</h1>
      <p className="mt-2 text-sm text-[#94A3B8]">{kind === "subscription" ? "Your first auto-buy has been credited. Future buys run automatically." : "Your BTC has been credited to your wallet."}</p><BackBtn /></Shell>
  );
  if (state === "failed" || state === "timeout") return (
    <Shell><XCircle className="mx-auto text-red-400" size={56} />
      <h1 className="mt-4 text-2xl font-bold tracking-tight">{state === "failed" ? "Payment failed" : "Still processing"}</h1>
      <p className="mt-2 text-sm text-[#94A3B8]">{state === "failed" ? "Your card was not charged." : "Check your dashboard in a moment."}</p><BackBtn /></Shell>
  );
  return (
    <Shell><Loader2 className="mx-auto animate-spin text-[#F7931A]" size={56} />
      <h1 className="mt-4 text-2xl font-bold tracking-tight">Confirming payment...</h1>
      <p className="mt-2 font-mono text-xs text-[#64748B]">{sid?.slice(0, 24)}...</p></Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell><XCircle className="mx-auto text-[#94A3B8]" size={56} />
      <h1 className="mt-4 text-2xl font-bold tracking-tight">Checkout cancelled</h1>
      <p className="mt-2 text-sm text-[#94A3B8]">No charge was made.</p><BackBtn /></Shell>
  );
}
